import React, { useState } from 'react';
import { Project, ProjectComment } from '../../../types';

interface ProjectActivitySidebarProps {
  project: Project;
  comments: ProjectComment[];
  onAddComment: (content: string) => Promise<void>;
}

const ProjectActivitySidebar: React.FC<ProjectActivitySidebarProps> = ({
  project,
  comments,
  onAddComment,
}) => {
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    setSubmitting(true);
    try {
      await onAddComment(commentText);
      setCommentText('');
    } catch (err) {
      console.error('Error adding comment:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-96 border-l border-gray-200 bg-gray-50">
      <div className="sticky top-0 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Activity</h3>
          <span className="text-xs text-gray-500">{comments.length}</span>
        </div>

        {/* Project Stats */}
        <div className="mb-6 pb-4 border-b border-gray-200">
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Tasks</span>
              <span className="font-medium text-gray-900">
                {project.stats.completedTasks}/{project.stats.totalTasks}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Team</span>
              <span className="font-medium text-gray-900">{project.members.length + 1}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-600">Views</span>
              <span className="font-medium text-gray-900">{project.stats.viewCount}</span>
            </div>
          </div>
        </div>

        {/* Add Comment */}
        <form onSubmit={handleSubmit} className="mb-4">
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Write a comment..."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={3}
          />
          <div className="mt-2 flex justify-end">
            <button
              type="submit"
              disabled={submitting || !commentText.trim()}
              className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {submitting ? 'Adding...' : 'Comment'}
            </button>
          </div>
        </form>

        {/* Comments List */}
        <div className="space-y-4">
          {comments.map((comment) => (
            <div key={comment._id} className="text-sm">
              <div className="flex items-start gap-2 mb-1">
                <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-medium flex-shrink-0">
                  {comment.author.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{comment.author.name}</span>
                    <span className="text-xs text-gray-500">
                      {new Date(comment.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-gray-700 mt-1 whitespace-pre-wrap break-words">{comment.content}</p>
                </div>
              </div>
            </div>
          ))}
          {comments.length === 0 && (
            <p className="text-sm text-gray-500 text-center py-8">No comments yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProjectActivitySidebar;
